import { Box, Theme, Typography } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { NextMeetingsOfParentReminderQueryQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import React from "react";

import { useNextMeetingsOfParentReminder } from "../../hooks/JoinMeeting";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import { LiveMeetingButtonWithVideo } from "../../routes/Classroom";
import { useLiveFromNow } from "./hooks/useLiveFromNow";

const JOIN_MEETING_TRACKING_EVENT = "join_meeting_from_parent_dashboard";
const START_MEETING_TRACKING_EVENT = "start_meeting_from_parent_dashboard";

function NextMeetingsReminder({
  data,
}: {
  data: NextMeetingsOfParentReminderQueryQuery["nextMeetingsOfParentReminder"][number];
}) {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\NextMeetingsOfParent",
  );
  const { timeZone: userTimeZone } = useTimeZone();
  const { currentUser } = useSession();
  const learner = currentUser?.children.find(
    child => child.uid === data.learnerUid,
  );

  const fromNow = useLiveFromNow(data.startTime);

  if (!learner) {
    return null;
  }

  return (
    <Box
      flex
      justifyContent="space-between"
      alignItems="center"
      flexDirection="column"
      sx={(theme: Theme) => ({
        backgroundColor: "primary.50",
        px: 16,
        py: 14,
        borderRadius: 0,
        "&:only-child": {
          borderRadius: 12,
        },
        "&:first-child:not(:only-child)": {
          borderRadius: "12px 12px 0 0",
        },
        "&:last-child:not(:only-child)": {
          borderRadius: "0 0 12px 12px",
        },
        gap: 8,
        [theme.breakpoints.up("md")]: {
          flexDirection: "row",
          gap: 0,
          padding: 20,
        },
      })}
    >
      <Box
        flex
        alignItems="center"
        flexDirection="column"
        sx={(theme: Theme) => ({
          gap: 8,
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            gap: 0,
          },
        })}
      >
        <Box flex alignItems="center">
          <AvatarImage
            avatar={learner.avatar || DEFAULT_AVATAR}
            name={learner.name}
            width={35}
            height={35}
          />
          <Typography variant="h6" sx={{ marginLeft: 12 }}>
            {learner.name}
          </Typography>
        </Box>
        <Box
          sx={(theme: Theme) => ({
            backgroundColor: "primary.200",
            borderRadius: "100%",
            width: 8,
            height: 8,
            marginLeft: 16,
            marginRight: 16,
            display: "none",
            [theme.breakpoints.up("md")]: {
              display: "block",
            },
          })}
        />
        <Typography
          variant="h6"
          sx={(theme: Theme) => ({
            textAlign: "center",
            [theme.breakpoints.up("md")]: {
              textAlign: "left",
            },
          })}
        >
          {data.activity.title}
        </Typography>
      </Box>
      <Box
        flex
        alignItems="center"
        flexDirection="column"
        sx={(theme: Theme) => ({
          gap: 8,
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            gap: 20,
          },
        })}
      >
        <Typography variant="subtitle2">
          {dayjs().isBefore(data.startTime) ? (
            <span>{t("Starting {{fromNow}}", { fromNow })}</span>
          ) : (
            <span>{t("Started {{fromNow}}", { fromNow })}</span>
          )}
        </Typography>
        {data.learnerUid && learner.name && (
          <LiveMeetingButtonWithVideo
            activity={data.activity as Activity}
            sectionUid={data.sectionUid}
            learnerUid={data.learnerUid}
            learnerName={learner.name}
            userTimeZone={userTimeZone}
            canManage={data.canManage}
            shouldShowOnlyButton
            joinButtonLabel={t("Join now")}
            joinTrackingLabel={
              data.canManage
                ? START_MEETING_TRACKING_EVENT
                : JOIN_MEETING_TRACKING_EVENT
            }
            size="small"
          />
        )}
      </Box>
    </Box>
  );
}

export default function NextMeetingsOfParent() {
  const { isLoggedIn } = useSession();
  const { data, loading } = useNextMeetingsOfParentReminder(!isLoggedIn);

  const nextMeetings = data?.nextMeetingsOfParentReminder;

  const nextMeetingsInDisplayRange = nextMeetings?.filter(data => {
    const startTime = dayjs(data.startTime);
    const fifteenMinutesAfterStart = startTime.add(15, "minutes");
    const fortyFiveMinutesBeforeStart = startTime.subtract(45, "minutes");

    return dayjs().isBetween(
      fortyFiveMinutesBeforeStart,
      fifteenMinutesAfterStart,
    );
  });

  if (
    loading ||
    !nextMeetingsInDisplayRange ||
    nextMeetingsInDisplayRange.length === 0
  ) {
    return null;
  }

  return (
    <Box sx={{ paddingBottom: 38 }}>
      {nextMeetingsInDisplayRange.map((data, index) => (
        <NextMeetingsReminder data={data} key={`${index}-${data.learnerUid}`} />
      ))}
    </Box>
  );
}
